import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { WeeklyScheduleEditor, DEFAULT_BUSINESS_SCHEDULE, EMPTY_SCHEDULE, normalizeSchedule } from '@/components/admin/WeeklyScheduleEditor';
import { PermissionTemplatesManager } from '@/components/admin/PermissionTemplatesManager';
import { PermissionMatrixEditor } from '@/components/admin/PermissionMatrixEditor';
import { PermissionPreview } from '@/components/admin/PermissionPreview';
import { toast } from 'sonner';
import { Plus, Pencil, Ban, PauseCircle, PlayCircle, Sparkles, ListChecks, Lock, Unlock, Eye } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { formatDateTime } from '@/lib/format';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

export default function AdminUsers() {
  const { isSuperAdmin, isCompanyAdmin } = useAuth();
  if (!isSuperAdmin && !isCompanyAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AppShell title="Users" subtitle={isSuperAdmin ? 'Manage users across companies (and other Super Admins).' : 'View users in your company. Only a Super Admin can create or edit users.'}>
      <UsersContent />
    </AppShell>
  );
}

function UsersContent() {
  const { user: me, isSuperAdmin, effectiveCompanyId } = useAuth();
  const [list, setList] = useState([]);
  const [permGroups, setPermGroups] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    email: '', password: '', name: '', role: 'user', company_id: '', permissions: [],
    permission_template_id: '',
    login_schedule_enabled: false, login_schedule: DEFAULT_BUSINESS_SCHEDULE,
  });
  const [delTarget, setDelTarget] = useState(null);
  const [pauseTarget, setPauseTarget] = useState(null);
  // Permission Templates: link-dropdown options for the user form + the
  // manager dialog (create/edit/delete).
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesMgrOpen, setTemplatesMgrOpen] = useState(false);
  const NO_TEMPLATE = '__no_template__';
  // Wave 3.99l — Preset role bundles fetched from the backend so we don't
  // duplicate the source of truth in the frontend. Each preset carries
  // ``{ id, label, description, permissions }``.
  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const canEdit = isSuperAdmin;

  const load = async () => {
    const params = {};
    if (isSuperAdmin && companyFilter) params.company_id = companyFilter;
    const { data } = await api.get('/users/', { params }); setList(data);
  };
  const loadMeta = async () => {
    const [p, c, pr] = await Promise.all([
      api.get('/users/permissions'),
      isSuperAdmin ? api.get('/companies/') : Promise.resolve({ data: [] }),
      api.get('/users/permission-presets').catch(() => ({ data: { presets: [] } })),
    ]);
    setPermGroups(p.data.groups || []);
    setAllPerms(p.data.all || []);
    setCompanies(c.data);
    setPresets(pr.data?.presets || []);
  };
  useEffect(() => { loadMeta(); load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyFilter]);

  // Load permission templates for the company selected in the user form so the
  // "Permission Template" link dropdown can offer them.
  const loadTemplatesFor = async (cid) => {
    if (!cid) { setTemplates([]); return; }
    setTemplatesLoading(true);
    try {
      const { data } = await api.get('/permission-templates', { params: { company_id: cid } });
      setTemplates(Array.isArray(data) ? data : []);
    } catch { setTemplates([]); }
    finally { setTemplatesLoading(false); }
  };
  useEffect(() => {
    if (open && form.company_id) loadTemplatesFor(form.company_id);
    else if (!open) setTemplates([]);
    /* eslint-disable-next-line */
  }, [open, form.company_id]);

  const openDialog = (u) => {
    setActivePresetId(null);
    if (u) {
      setEditing(u); setForm({
        email: u.email, password: '', name: u.name, role: u.role,
        company_id: u.company_id || '', permissions: u.permissions || [],
        permission_template_id: u.permission_template_id || '',
        login_schedule_enabled: !!u.login_schedule_enabled,
        login_schedule: normalizeSchedule(u.login_schedule || DEFAULT_BUSINESS_SCHEDULE),
      });
    } else {
      setEditing(null);
      setForm({
        email: '', password: '', name: '', role: 'user',
        company_id: '',
        // Default for new "user" role: read-only on common modules
        permissions: ['vendors.view', 'goldsmiths.view', 'diamonds.packets.view', 'gold.view', 'orders.view', 'reports.view'],
        permission_template_id: '',
        login_schedule_enabled: false,
        login_schedule: DEFAULT_BUSINESS_SCHEDULE,
      });
    }
    setOpen(true);
  };

  const submit = async () => {
    try {
      const isSales = form.role === 'sales_person' || form.role === 'sales_manager';
      const isUserRole = form.role === 'user';
      // Sales roles get a fixed view-only permission on the backend; users
      // linked to a template inherit the template's permissions.
      const linkedTpl = isUserRole ? (form.permission_template_id || '') : '';
      if (editing) {
        const payload = {
          name: form.name, role: form.role,
          login_schedule_enabled: form.login_schedule_enabled,
          login_schedule: form.login_schedule,
        };
        if (isSales) {
          payload.permissions = ['orders.view'];
        } else if (isUserRole) {
          // Send the link value explicitly so an empty string UNLINKS. When a
          // template is linked, the backend snapshots its permissions.
          payload.permission_template_id = linkedTpl;
          if (!linkedTpl) payload.permissions = form.permissions;
        } else {
          payload.permissions = form.permissions;
        }
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        const payload = { ...form };
        if (isSales) {
          payload.permissions = ['orders.view'];
          delete payload.permission_template_id;
        } else if (isUserRole) {
          if (linkedTpl) {
            payload.permission_template_id = linkedTpl;
            delete payload.permissions;
          } else {
            delete payload.permission_template_id;
          }
        } else {
          delete payload.permission_template_id;
        }
        if (payload.role === 'super_admin') delete payload.company_id;
        await api.post('/users/', payload);
      }
      toast.success(editing ? 'User updated' : 'User created');
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  // Wave 3.99l — Apply a preset by REPLACING the current permissions
  // array with the preset's list. Super Admin can still tweak
  // individual cells afterwards; ``activePresetId`` is cleared on any
  // manual edit so the badge doesn't lie.
  const applyPreset = (preset) => {
    if (!preset) return;
    setForm((f) => ({ ...f, permissions: [...(preset.permissions || [])] }));
    setActivePresetId(preset.id);
  };

  // Apply a saved Permission Template's permissions as a ONE-OFF starting
  // point (a copy). Unlike the "Link to Permission Template" dropdown (which
  // keeps the user live-synced to the template), this just seeds the matrix
  // so the admin can tweak from there. ``activePresetId`` is namespaced with
  // ``tpl:`` so the chip highlights independently of the built-in presets.
  const applyTemplateAsPreset = (tpl) => {
    if (!tpl) return;
    setForm((f) => ({ ...f, permissions: [...(tpl.permissions || [])] }));
    setActivePresetId(`tpl:${tpl.id}`);
  };

  // Phase 8.4 — Resume a paused user's login (Super Admin only)
  const resumeUser = async (u) => {
    try {
      await api.post(`/users/${u.id}/resume`);
      toast.success(`${u.name || u.email} login resumed`);
      load();
    } catch (e) { toast.error(extractError(e, 'Failed to resume user')); }
  };

  // Account Lockout — unlock a user locked out by failed logins (Super Admin only)
  const unlockUser = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.name || u.email} account unlocked`);
      load();
    } catch (e) { toast.error(extractError(e, 'Failed to unlock user')); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">All Users</div>
          <div className="text-xs text-slate-500">
            {canEdit
              ? 'Roles: Super Admin (cross-company), Company Admin (own company), User (limited).'
              : 'Only a Super Admin can create, edit or disable portal users. Contact your Super Admin to request changes.'}
          </div>
        </div>
        <div className="flex gap-2 items-end">
          {isSuperAdmin && (
            <div>
              <Label className="text-[11px] text-slate-500">Company</Label>
              <div className="w-52">
                <SearchableSelect
                  items={[{ id: 'all', name: 'All companies' }, ...companies]}
                  value={companyFilter || 'all'}
                  onValueChange={(v) => setCompanyFilter(!v || v === 'all' ? '' : v)}
                  itemToLabel={(c) => c.name}
                  searchKeys={['name']}
                  placeholder="All companies"
                  searchPlaceholder="Search company…"
                  testId="admin-users-company-filter"
                />
              </div>
            </div>
          )}
          {isSuperAdmin && (
            <Button
              variant="outline"
              className="h-9"
              onClick={() => setTemplatesMgrOpen(true)}
              data-testid="manage-templates-button"
              title="Create and manage reusable permission templates"
            >
              <ListChecks size={14} className="mr-1.5" /> Permission Templates
            </Button>
          )}
          {canEdit && <Button className="luxury-button h-9" onClick={() => openDialog(null)} data-testid="user-add-button"><Plus size={14} className="mr-1.5" /> New User</Button>}
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">User</th>
              <th className="text-left">Role</th>
              <th className="text-left">Company</th>
              <th className="text-left">Status</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="users-table-body">
              {list.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No users yet.</td></tr>}
              {list.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td>
                    <Badge variant="secondary" className="capitalize">{u.role.replace('_',' ')}</Badge>
                  </td>
                  <td className="text-sm">{u.company_name || (u.role === 'super_admin' ? 'All (Super Admin)' : '—')}</td>
                  <td>
                    {u.disabled
                      ? <Badge className="bg-rose-50 text-rose-700">Disabled</Badge>
                      : (u.login_paused
                          ? <Badge className="bg-amber-50 text-amber-700" data-testid={`user-paused-badge-${u.id}`}>
                              <PauseCircle size={10} className="mr-1 inline" />
                              {u.pause_indefinite ? 'Paused (Indefinite)' : 'Paused'}
                            </Badge>
                          : <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>)
                    }
                    {u.login_paused && !u.pause_indefinite && u.pause_to && (
                      <div className="text-[10px] text-slate-500 mt-0.5" title={`From ${u.pause_from || '—'} to ${u.pause_to}`}>until {formatDateTime(u.pause_to)}</div>
                    )}
                    {u.locked && (
                      <Badge className="bg-rose-100 text-rose-700 border border-rose-200 ml-1 align-middle" data-testid={`user-locked-badge-${u.id}`}>
                        <Lock size={10} className="mr-1 inline" />Locked
                      </Badge>
                    )}
                  </td>
                  <td className="text-right">
                    {canEdit ? (
                      <div className="inline-flex gap-2 items-center flex-wrap justify-end">
                        <button onClick={() => openDialog(u)} className="text-xs text-[var(--ink-navy)] hover:underline" data-testid={`user-edit-${u.id}`}><Pencil size={12} className="inline mr-1" />Edit</button>
                        {isSuperAdmin && u.locked && u.id !== me.id && (
                          <button onClick={() => unlockUser(u)} className="text-xs text-emerald-700 hover:underline" data-testid={`user-unlock-${u.id}`}><Unlock size={12} className="inline mr-1" />Unlock</button>
                        )}
                        {u.role !== 'super_admin' && u.id !== me.id && !u.disabled && (
                          u.login_paused
                            ? <button onClick={() => resumeUser(u)} className="text-xs text-emerald-700 hover:underline" data-testid={`user-resume-${u.id}`}><PlayCircle size={12} className="inline mr-1" />Resume</button>
                            : <button onClick={() => setPauseTarget(u)} className="text-xs text-amber-700 hover:underline" data-testid={`user-pause-${u.id}`}><PauseCircle size={12} className="inline mr-1" />Pause</button>
                        )}
                        {u.id !== me.id && !u.disabled && <button onClick={() => setDelTarget(u)} className="text-xs text-rose-600 hover:underline" data-testid={`user-delete-${u.id}`}><Ban size={12} className="inline mr-1" />Disable</button>}
                      </div>
                    ) : (
                      <span className="text-[11px] text-slate-400">Read-only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'New User'}</DialogTitle><DialogDescription>{editing ? 'Update profile, role, or permissions.' : 'Create a portal user with role-based access.'}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>{editing ? 'New Password (leave blank to keep)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="sales_person">Sales Person</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="company_admin">Company Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role !== 'super_admin' && (
              <div>
                <Label>Company</Label>
                <SearchableSelect
                  items={companies}
                  value={form.company_id}
                  onValueChange={(v) => setForm({ ...form, company_id: v || '' })}
                  itemToLabel={(c) => c.name}
                  searchKeys={['name']}
                  placeholder="Select company"
                  searchPlaceholder="Search company…"
                  disabled={companies.length === 0}
                  testId="user-company-select"
                />
              </div>
            )}

            {/* Permission Template link — only for standard users. Linking
                delegates permissions to the template (live-linked). */}
            {form.role === 'user' && (
              <div className="col-span-2">
                <Label>Permission Template</Label>
                <Select
                  value={form.permission_template_id || NO_TEMPLATE}
                  onValueChange={(v) => setForm((f) => ({ ...f, permission_template_id: v === NO_TEMPLATE ? '' : v }))}
                  disabled={!form.company_id || templatesLoading}
                >
                  <SelectTrigger data-testid="user-template-select"><SelectValue placeholder="Custom permissions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TEMPLATE}>Custom permissions (no template)</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`user-template-option-${t.id}`}>
                        {t.name} · {(t.permissions || []).length} perms
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Link this user to a saved template — editing the template later updates every linked user instantly.
                  {!form.company_id && ' Select a company first.'}
                </div>
              </div>
            )}

            {/* Sales role note — no permission matrix applies. */}
            {(form.role === 'sales_person' || form.role === 'sales_manager') && (
              <div className="col-span-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5 text-[12px] text-indigo-800" data-testid="user-sales-role-note">
                <div className="font-medium mb-0.5">View-only, customer-scoped role</div>
                This user will see ONLY the active orders of the customers they are attributed to (assign under
                {' '}<span className="font-medium">Customers → Sales attribution</span>). They can post comments &amp; @mention users, but cannot edit orders, view completed/received-back/on-hold orders, or export PDFs. No permission matrix applies.
              </div>
            )}

            {/* Template-linked summary — permissions managed by the template. */}
            {form.role === 'user' && form.permission_template_id && (
              <div className="col-span-2 space-y-2">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12px] text-emerald-800" data-testid="user-template-linked-note">
                  {(() => {
                    const lt = templates.find((t) => t.id === form.permission_template_id);
                    return `Permissions are managed by the linked template${lt ? ` "${lt.name}" (${(lt.permissions || []).length} permissions)` : ''}. Set the dropdown to "Custom permissions" to edit them manually here, or edit the template to update all linked users at once.`;
                  })()}
                </div>
                {/* Read-only preview of what the linked template grants. */}
                <div className="rounded-md border border-[var(--divider)] bg-slate-50/60 p-3 max-h-[52vh] overflow-y-auto" data-testid="user-linked-preview-panel">
                  <div className="text-sm font-medium text-[var(--ink-navy)] inline-flex items-center gap-1.5 mb-2">
                    <Eye size={14} /> Access preview
                  </div>
                  <PermissionPreview
                    permissions={(templates.find((t) => t.id === form.permission_template_id) || {}).permissions || []}
                    idPrefix="user-linked-preview"
                  />
                </div>
              </div>
            )}

            {form.role !== 'sales_person' && form.role !== 'sales_manager' && !(form.role === 'user' && form.permission_template_id) && (
            <div className="col-span-2">
              {/* Matrix + live access preview, side-by-side so admins see the
                  real-world effect while choosing permissions. Stacks on narrow
                  screens. */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
                <div className="min-w-0">
              <PermissionMatrixEditor
                permissions={form.permissions}
                onChange={(next) => { setActivePresetId(null); setForm((f) => ({ ...f, permissions: next })); }}
                permGroups={permGroups}
                allKeys={allPerms}
                idPrefix="user"
              >
              {/* Wave 3.99l — Preset role chips. Small badge-style, one
                  click replaces the permission set with the preset. */}
              {presets.length > 0 && (
                <div className="mb-3" data-testid="user-preset-chips">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                    <Sparkles size={11} className="text-[var(--ink-navy)]" /> Preset Roles
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {presets.map((preset) => {
                      const isActive = activePresetId === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => applyPreset(preset)}
                          title={preset.description}
                          data-testid={`user-preset-${preset.id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors ${
                            isActive
                              ? 'bg-[var(--ink-navy)] text-white border-[var(--ink-navy)]'
                              : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400'
                          }`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Applying a preset replaces the entire permission set. You can still tweak individual cells afterwards.
                  </div>
                </div>
              )}

              {/* Saved Permission Templates as selectable presets. Clicking a
                  template chip copies its permissions into the matrix (one-off).
                  Use the "Permission Template" dropdown above to instead LINK
                  the user so they stay auto-synced when the template changes. */}
              {form.role === 'user' && templates.length > 0 && (
                <div className="mb-3" data-testid="user-template-presets">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                    <ListChecks size={11} className="text-[var(--ink-navy)]" /> Your Templates
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {templates.map((t) => {
                      const isActive = activePresetId === `tpl:${t.id}`;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => applyTemplateAsPreset(t)}
                          title={`Apply ${(t.permissions || []).length} permissions from "${t.name}"`}
                          data-testid={`user-template-preset-${t.id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors ${
                            isActive
                              ? 'bg-[var(--gold-accent)] text-white border-[var(--gold-accent)]'
                              : 'bg-white text-slate-700 border-slate-300 hover:bg-[var(--gold-bg)] hover:border-[var(--gold-accent-soft)]'
                          }`}
                        >
                          {t.name}
                          <span className={`text-[9px] ${isActive ? 'text-white/80' : 'text-slate-400'}`}>· {(t.permissions || []).length}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Copies the template’s permissions as a starting point. To keep this user auto-synced with future template edits, use the “Permission Template” dropdown above instead.
                  </div>
                </div>
              )}

              </PermissionMatrixEditor>
              {form.role !== 'user' && <div className="text-[11px] text-slate-500 mt-1">Admins typically have all permissions.</div>}
                </div>
                <div
                  className="lg:sticky lg:top-2 self-start rounded-md border border-[var(--divider)] bg-slate-50/60 p-3 lg:max-h-[64vh] lg:overflow-y-auto"
                  data-testid="user-editor-preview-panel"
                >
                  <div className="text-sm font-medium text-[var(--ink-navy)] inline-flex items-center gap-1.5 mb-2">
                    <Eye size={14} /> Access preview
                  </div>
                  <PermissionPreview permissions={form.permissions} idPrefix="user-editor-preview" />
                </div>
              </div>
            </div>
            )}

            {/* Item 3 — Per-user weekly login schedule (allowed windows, IST). */}
            {form.role !== 'super_admin' && (
              <div className="col-span-2" data-testid="user-login-schedule-section">
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <Label>Login Schedule (IST)</Label>
                    <div className="text-[11px] text-slate-500">
                      Restrict when this user can log in. Outside the allowed windows they can neither log in nor use an existing session. Times are IST (GMT+5:30).
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer shrink-0">
                    <span className="text-slate-600">{form.login_schedule_enabled ? 'Enabled' : 'Disabled'}</span>
                    <Switch
                      checked={form.login_schedule_enabled}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, login_schedule_enabled: !!v }))}
                      data-testid="user-login-schedule-toggle"
                    />
                  </label>
                </div>
                {form.login_schedule_enabled && (
                  <div data-testid="user-login-schedule-editor-wrap">
                    <div className="flex items-center justify-end gap-2 mb-2">
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => setForm((f) => ({ ...f, login_schedule: DEFAULT_BUSINESS_SCHEDULE }))} data-testid="user-login-schedule-default">Reset to default</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => setForm((f) => ({ ...f, login_schedule: EMPTY_SCHEDULE }))} data-testid="user-login-schedule-clear">Clear all</Button>
                    </div>
                    <WeeklyScheduleEditor
                      value={form.login_schedule}
                      onChange={(next) => setForm((f) => ({ ...f, login_schedule: next }))}
                      testId="user-login-schedule-editor"
                    />
                    <div className="text-[10px] text-slate-500 mt-1">Default: Mon–Fri 09:30–19:00, Sat 09:30–17:00, Sunday closed.</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="luxury-button" disabled={!form.email || (!editing && !form.password) || !form.name || (form.role !== 'super_admin' && !form.company_id)} data-testid="user-submit-button">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="user"
          entity_id={delTarget.id}
          entity_label={delTarget.email}
          company_id={delTarget.company_id}
          onSuccess={() => { setDelTarget(null); load(); }}
        />
      )}

      {pauseTarget && (
        <PauseUserDialog
          target={pauseTarget}
          onClose={() => setPauseTarget(null)}
          onDone={() => { setPauseTarget(null); load(); }}
        />
      )}

      {isSuperAdmin && (() => {
        // Resolve the company for template management: prefer the page's
        // Company filter, else fall back to the globally-selected company
        // (top-right Company Switcher) so the manager works even when the
        // page filter is left on "All companies".
        const tplCompanyId = companyFilter || effectiveCompanyId || '';
        const tplCompanyName = (companies.find((c) => c.id === tplCompanyId) || {}).name
          || (tplCompanyId === effectiveCompanyId ? me?.company_name : '');
        return (
          <PermissionTemplatesManager
            open={templatesMgrOpen}
            onOpenChange={setTemplatesMgrOpen}
            companyId={tplCompanyId}
            companyName={tplCompanyName}
            permGroups={permGroups}
            onChanged={() => { if (open && form.company_id) loadTemplatesFor(form.company_id); }}
          />
        );
      })()}
    </div>
  );
}

// ============================================================================
// Phase 8.4 — Pause User Dialog
// ============================================================================
function PauseUserDialog({ target, onClose, onDone }) {
  const [indefinite, setIndefinite] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { indefinite, reason };
      if (!indefinite) {
        if (!toDate) { toast.error('Pause-To date is required'); setBusy(false); return; }
        // Build full ISO datetimes (start = 00:00 UTC, end = 23:59:59 UTC) so the SA can be timezone-agnostic
        payload.paused_from = fromDate ? new Date(fromDate + 'T00:00:00Z').toISOString() : new Date().toISOString();
        payload.paused_to = new Date(toDate + 'T23:59:59Z').toISOString();
      }
      await api.post(`/users/${target.id}/pause`, payload);
      toast.success(`${target.name || target.email} login paused`);
      onDone();
    } catch (e) { toast.error(extractError(e, 'Pause failed')); }
    finally { setBusy(false); }
  };

  const canSubmit = !busy && (indefinite || (!!toDate));

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pause-user-dialog">
        <DialogHeader>
          <DialogTitle>Pause user login</DialogTitle>
          <DialogDescription>
            Pause login access for <span className="font-medium text-slate-700">{target.name || target.email}</span>{' '}
            ({target.role.replace('_', ' ')}). They will see a clear message on attempting to login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pause-indefinite-toggle">
            <Checkbox checked={indefinite} onCheckedChange={(v) => setIndefinite(!!v)} />
            <span>Indefinite — pause until manually resumed by Super Admin</span>
          </label>
          {!indefinite && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From (optional)</Label>
                <DatePicker value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="pause-from-input" />
                <div className="text-[10px] text-slate-500 mt-1">Defaults to now if blank.</div>
              </div>
              <div>
                <Label>To <span className="text-rose-600">*</span></Label>
                <DatePicker value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="pause-to-input" />
                <div className="text-[10px] text-slate-500 mt-1">Auto-restores access after this date.</div>
              </div>
            </div>
          )}
          <div>
            <Label>Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pause-reason-input" placeholder="e.g. On leave / under review" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} className="luxury-button" disabled={!canSubmit} data-testid="pause-submit-button">
            {busy ? 'Pausing…' : 'Pause Login'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
